import { localize } from '../utils/Localization';
import * as ReplayManager from '../utils/ReplayManager';
import * as AudioManager from '../utils/AudioManager';
import { Settings } from '../Settings';
import { openExternalLink } from '../utils/ExternalLinks';
import '../css/ReplayControls.scss';

const icon = path => `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${path}"/></svg>`;
const PLAY = icon('M8 5v14l11-7z');
const PAUSE = icon('M6 19h4V5H6v14zm8-14v14h4V5h-4z');
const buttons = [
	['restart', 'replay_restart', icon('M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z')],
	['slower', 'replay_slower', icon('M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z')],
	['toggle', 'replay_pause', PAUSE],
	['faster', 'replay_faster', icon('M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z')],
	['step', 'replay_step', icon('M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z')],
	['link', 'replay_copy_link', icon('M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z')],
	['video', 'replay_make_video', icon('M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z')],
	['exit', 'replay_exit', icon('M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z')],
];

let element = null;
let soundButton = null;
let handlers = null;
let shown = '';
let toastTimer = null;
let finish = null;

const part = name => element.querySelector(`.replay-${name}`);

const changeSpeed = direction => {
	const status = ReplayManager.replayStatus();
	if(!status) return;
	const speeds = ReplayManager.REPLAY_SPEEDS;
	const index = Math.max(0, Math.min(speeds.length - 1, speeds.indexOf(status.speed) + direction));
	ReplayManager.setReplaySpeed(speeds[index]);
};

const toast = text => {
	part('toast').textContent = text;
	part('toast').hidden = false;
	clearTimeout(toastTimer);
	toastTimer = setTimeout(() => { part('toast').hidden = true; }, 2000);
};

// Plain http test builds have no share sheet or clipboard.
export const shareReplayLink = async (link, onCopied) => {
	const url = await link;
	if(!url) return;
	if(navigator.share && matchMedia('(pointer: coarse)').matches){
		try {
			await navigator.share({url});
			return;
		} catch(error) {
			if(error.name === 'AbortError') return;
		}
	}
	try {
		await navigator.clipboard.writeText(url);
		onCopied?.();
	} catch {
		window.prompt(localize('replay_copy_link'), url);
	}
};

// The replay editor page renders the uploaded replay to a video file.
const openReplayEditor = async link => {
	const url = await link;
	if(!url) return;
	const params = new URL(url).searchParams;
	const id = params.get('replay') || params.get('gdreplay');
	if(id) openExternalLink(new URL(`replay-editor/?replay=${encodeURIComponent(id)}`, document.baseURI).href);
};

const actions = {
	restart: () => handlers.restart(),
	slower: () => changeSpeed(-1),
	faster: () => changeSpeed(1),
	toggle: () => {
		const status = ReplayManager.replayStatus();
		if(status?.ended) handlers.restart();
		else if(status) ReplayManager.setReplayPaused(!status.paused);
	},
	step: () => {
		ReplayManager.setReplayPaused(true);
		ReplayManager.stepReplay();
	},
	link: () => shareReplayLink(handlers.link(), () => toast(localize('multiplayer_copied'))),
	video: () => {
		ReplayManager.setReplayPaused(true);
		openReplayEditor(handlers.link());
	},
	exit: () => handlers.exit(),
	sound: () => {
		AudioManager.unlockAudio();
		soundButton.hidden = true;
	},
};

const create = () => {
	element = document.createElement('div');
	element.className = 'replay-controls';
	element.hidden = true;
	element.innerHTML = `
		<div class="replay-info">
			<span class="replay-player"></span>
			<span class="replay-level"></span>
			<span class="replay-attempt"></span>
			<span class="replay-sync" hidden>${localize('replay_out_of_sync')}</span>
			<span class="replay-finish" hidden></span>
		</div>
		<div class="replay-progress"><div class="replay-progress-fill"></div></div>
		<div class="replay-buttons">
			${buttons.map(([action, label, svg]) => `<button type="button" data-action="${action}" title="${localize(label)}" aria-label="${localize(label)}">${svg}</button>`).join('')}
			<span class="replay-speed"></span>
		</div>
		<div class="replay-end" hidden>
			<button type="button" data-action="restart">${localize('replay_restart')}</button>
			<button type="button" data-action="exit">${localize('replay_play_level')}</button>
		</div>
		<div class="replay-toast" role="status" hidden></div>`;
	element.querySelector('.replay-buttons').insertBefore(part('speed'), element.querySelector('[data-action="step"]'));
	soundButton = document.createElement('button');
	soundButton.type = 'button';
	soundButton.className = 'replay-sound';
	soundButton.dataset.action = 'sound';
	soundButton.hidden = true;
	soundButton.innerHTML = `<span class="replay-sound-icon" aria-hidden="true"></span>${localize('replay_sound')}`;
	[...element.querySelectorAll('button'), soundButton].forEach(button => {
		// Keep focus on the game, so Space never also presses the focused button.
		button.onmousedown = event => event.preventDefault();
		button.onclick = () => actions[button.dataset.action]();
	});
	document.body.append(element, soundButton);
};

export const show = replayHandlers => {
	if(!element) create();
	handlers = replayHandlers;
	shown = '';
	finish = null;
	element.hidden = false;
	update();
};

export const hide = () => {
	if(element) element.hidden = true;
	if(soundButton) soundButton.hidden = true;
	handlers = null;
};

export const showFinish = time => {
	finish = time;
	shown = '';
};

export const update = () => {
	const status = ReplayManager.replayStatus();
	if(!element || element.hidden || !status) return;
	const {replay, attempt, frame, speed, paused, ended, mismatch} = status;
	const attempts = replay.attempts;
	let total = 0, done = frame;
	attempts.forEach((item, index) => {
		total += item.frames;
		if(index < attempt) done += item.frames;
	});
	part('progress-fill').style.width = `${total ? 100 * Math.min(1, done / total) : 0}%`;
	const muted = Settings.sfxOn && AudioManager.audioLocked();
	if(soundButton.hidden === muted) soundButton.hidden = !muted;
	if(muted){
		const panel = element.getBoundingClientRect();
		const below = window.innerWidth - panel.right < soundButton.offsetWidth + 20;
		soundButton.classList.toggle('below', below);
		soundButton.style.top = below ? `${panel.bottom + 8}px` : '';
	}
	const key = [attempt, speed, paused || ended, !!mismatch, finish].join();
	if(key === shown) return;
	shown = key;
	part('player').textContent = replay.player ? localize('replay_watching', {name: replay.player}) : localize('replay_title');
	part('level').textContent = replay.level.title || '';
	part('attempt').textContent = attempts.length > 1 ? localize('replay_attempt', {current: attempt + 1, total: attempts.length}) : '';
	part('sync').hidden = !mismatch;
	part('finish').hidden = !finish;
	part('finish').textContent = finish ? localize('replay_finished', {time: finish}) : '';
	part('end').hidden = !ended;
	part('speed').textContent = `${speed}×`;
	const toggle = element.querySelector('[data-action="toggle"]');
	const label = localize(paused || ended ? 'replay_play' : 'replay_pause');
	toggle.innerHTML = paused || ended ? PLAY : PAUSE;
	toggle.title = label;
	toggle.setAttribute('aria-label', label);
};

export const onKeyDown = event => {
	if(event.ctrlKey || event.metaKey) return false;
	// Howler only unlocks audio on taps and clicks.
	AudioManager.unlockAudio();
	if(!event.repeat){
		const action = {' ': 'toggle', ArrowLeft: 'slower', ArrowRight: 'faster', '.': 'step', r: 'restart', R: 'restart', Escape: 'exit'}[event.key];
		if(action) actions[action]();
	}
	event.preventDefault();
	return true;
};
